import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Collapse,
  DatePicker,
  Descriptions,
  Drawer,
  Empty,
  message,
  Select,
  Skeleton,
  Space,
  Table,
  Tag,
  Typography,
} from "antd";
import dayjs from "dayjs";
import {
  ArrowLeftOutlined,
  DollarOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "contexts/AuthContext";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import paths from "routes/paths";
import { fetchWorkspace } from "services/workspaceService";
import olreadiaService from "services/olreadiaService";
import paymentChargesService from "services/paymentChargesService";
import {
  organizationName,
  readable,
} from "./workspaceUtils";
import { OLIVEEB_COLORS, workspaceButtonStyles, workspaceStyles } from "./colors";

const { Text } = Typography;
const { RangePicker } = DatePicker;

const PAGE_SIZE = 20;
const DEFAULT_TIME_ZONE = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
const FALLBACK_TIME_ZONES = [
  "UTC",
  "America/Los_Angeles",
  "America/Denver",
  "America/Chicago",
  "America/New_York",
  "Europe/London",
  "Europe/Paris",
  "Africa/Lagos",
  "Asia/Dubai",
  "Asia/Kolkata",
  "Asia/Tokyo",
  "Australia/Sydney",
];
const TIME_ZONE_OPTIONS = Array.from(
  new Set([
    DEFAULT_TIME_ZONE,
    ...(
      typeof Intl.supportedValuesOf === "function"
        ? Intl.supportedValuesOf("timeZone")
        : FALLBACK_TIME_ZONES
    ),
  ]),
).map((zone) => ({ value: zone, label: zone }));

const pageContent = (data) => (Array.isArray(data) ? data : data?.content || []);

const pageTotal = (data) =>
  typeof data?.totalElements === "number" ? data.totalElements : pageContent(data).length;

const bgpCodeOf = (charge) =>
  charge?.bgpCode ||
  charge?.businessGrowthPartnerCode ||
  charge?.paymentChargeDetails?.bgpCode ||
  charge?.paymentChargeDetails?.businessGrowthPartnerCode ||
  charge?.chargeDetails?.bgpCode ||
  charge?.chargeDetails?.businessGrowthPartnerCode ||
  "";

const listFrom = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (value === undefined || value === null || value === "") return [];
  return [value];
};

const uniqueStrings = (values) => Array.from(
  new Set(values.map((value) => String(value || "").trim()).filter(Boolean)),
);

const appliedCouponsOf = (charge) => [
  ...listFrom(charge?.appliedCoupons),
  ...listFrom(charge?.paymentChargeDetails?.appliedCoupons),
  ...listFrom(charge?.chargeDetails?.appliedCoupons),
].filter(Boolean);

const couponCodesOf = (charge) => uniqueStrings([
  ...listFrom(charge?.couponCodes),
  ...appliedCouponsOf(charge).map((coupon) => coupon?.code),
  charge?.couponCode,
  charge?.coupon?.code,
  charge?.discountCouponCode,
  charge?.discountCode,
  charge?.promoCode,
  charge?.promotionCode,
  ...listFrom(charge?.paymentChargeDetails?.couponCodes),
  charge?.paymentChargeDetails?.couponCode,
  charge?.paymentChargeDetails?.coupon?.code,
  charge?.paymentChargeDetails?.discountCouponCode,
  charge?.paymentChargeDetails?.discountCode,
  charge?.paymentChargeDetails?.promoCode,
  charge?.paymentChargeDetails?.promotionCode,
  ...listFrom(charge?.chargeDetails?.couponCodes),
  charge?.chargeDetails?.couponCode,
  charge?.chargeDetails?.coupon?.code,
  charge?.chargeDetails?.discountCouponCode,
  charge?.chargeDetails?.discountCode,
  charge?.chargeDetails?.promoCode,
  charge?.chargeDetails?.promotionCode,
]);

const couponLabelOf = (charge) =>
  charge?.coupon?.name ||
  charge?.couponName ||
  charge?.discountName ||
  charge?.promotionName ||
  charge?.paymentChargeDetails?.coupon?.name ||
  charge?.paymentChargeDetails?.couponName ||
  charge?.paymentChargeDetails?.discountName ||
  charge?.paymentChargeDetails?.promotionName ||
  charge?.chargeDetails?.coupon?.name ||
  charge?.chargeDetails?.couponName ||
  charge?.chargeDetails?.discountName ||
  charge?.chargeDetails?.promotionName ||
  "";

const discountAmountOf = (charge) => {
  const explicitAmount =
    charge?.couponDiscountTotal ??
    charge?.discountAmount ??
    charge?.couponDiscountAmount ??
    charge?.paymentChargeDetails?.couponDiscountTotal ??
    charge?.paymentChargeDetails?.discountAmount ??
    charge?.paymentChargeDetails?.couponDiscountAmount ??
    charge?.chargeDetails?.couponDiscountTotal ??
    charge?.chargeDetails?.discountAmount ??
    charge?.chargeDetails?.couponDiscountAmount ??
    null;

  if (explicitAmount !== null && explicitAmount !== undefined && explicitAmount !== "") {
    return explicitAmount;
  }

  const appliedTotal = appliedCouponsOf(charge).reduce(
    (total, coupon) => total + Number(coupon?.discountAmount || 0),
    0,
  );
  return appliedTotal || null;
};

const discountRateOf = (charge) =>
  charge?.discountRate ??
  charge?.discountPercentage ??
  charge?.couponDiscountRate ??
  charge?.paymentChargeDetails?.discountRate ??
  charge?.paymentChargeDetails?.discountPercentage ??
  charge?.paymentChargeDetails?.couponDiscountRate ??
  charge?.chargeDetails?.discountRate ??
  charge?.chargeDetails?.discountPercentage ??
  charge?.chargeDetails?.couponDiscountRate ??
  null;

const jsonPreview = (value) => {
  if (!value || typeof value !== "object") return "-";
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return "Unable to display details.";
  }
};

const lastSecondOfDay = (value) => value.hour(23).minute(59).second(59).millisecond(0);

const defaultDateRange = () => [dayjs().subtract(90, "day").startOf("day"), lastSecondOfDay(dayjs())];

const dateRangeToParams = (range) => {
  const [start, end] = range || [];
  return {
    start: start ? start.startOf("day").toISOString() : undefined,
    end: end ? lastSecondOfDay(end).toISOString() : undefined,
  };
};

const dateRangeLabel = (range) => {
  const [start, end] = range || [];
  if (!start || !end) return "All available dates";
  return `${start.format("MMM D, YYYY HH:mm:ss")} - ${end.format("MMM D, YYYY HH:mm:ss")}`;
};

const fullLocalTimestamp = (value, timeZone = DEFAULT_TIME_ZONE) => {
  if (!value) return "-";
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return "-";
  return new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    timeZoneName: "short",
    timeZone,
  }).format(date);
};

const money = (value, currency = "USD") => {
  if (value === undefined || value === null || value === "") return "-";
  const number = Number(value);
  if (!Number.isFinite(number)) return String(value);
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: currency || "USD",
  }).format(number);
};

const nameOf = (user, fallback = "-") => {
  const name = [user?.firstName, user?.middleName, user?.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();
  return name || user?.email || user?.id || fallback;
};

const activeAccountUserId = (account, fallbackUserId) => {
  const users = account?.users || [];
  const active = users.find((member) => {
    const status = String(member?.membershipStatus || "").toUpperCase();
    return status === "ACTIVE" || status === "ACCEPTED" || status === "";
  });
  return active?.user?.id || fallbackUserId || "";
};

const isPersonalAccount = (account) =>
  String(account?.accountType || "").toUpperCase() === "PERSONAL";

const prettyChargeItemLabel = (code) => {
  if (!code) return "General Charge";
  switch (code) {
    case "OLITASK_TASK_PAYMENT":
      return "Task Payment";
    case "OLITASK_HELP_REQUEST":
      return "Help Request Payment";
    case "OLITASK_HELP_TIP":
      return "Helper Tip";
    case "OLITASK_SERVICE_FEE":
      return "Service Fee";
    case "OLITASK_DONATION":
      return "Donation";
    default:
      return readable(code, "General Charge");
  }
};

const incomingDisplayAmount = (charge) =>
  Number(charge?.receivableAmount ?? charge?.amount ?? 0);

const incomingDetailAmount = incomingDisplayAmount;

const incomingTipAmount = (charge) => {
  if (charge?.chargeItemCode === "OLITASK_HELP_TIP") {
    return Number(charge?.receivableAmount ?? charge?.amount ?? 0);
  }
  return null;
};

const humanizeDetailLabel = (key) =>
  String(key || "")
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (char) => char.toUpperCase())
    .trim();

const tryParseJsonString = (value) => {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed || (!trimmed.startsWith("{") && !trimmed.startsWith("["))) return null;
  try {
    return JSON.parse(trimmed);
  } catch {
    return null;
  }
};

const formatChargeDetailValue = (key, value, currency = "USD") => {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "number") {
    if (/amount|subtotal|charge|receivable|credit|tip|payout|total/i.test(key)) {
      return money(value, currency);
    }
    return String(value);
  }
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "string") {
    const parsed = tryParseJsonString(value);
    if (parsed) return formatChargeDetailValue(key, parsed, currency);
    return value.trim();
  }
  if (Array.isArray(value)) return value.length ? `${value.length} item(s)` : null;
  if (typeof value === "object") {
    const summary = value.name || value.code || value.label || value.id;
    return typeof summary === "string" && summary.trim() ? summary.trim() : null;
  }
  return null;
};

const paymentMethodRows = (method) => {
  if (!method) return [];
  return [
    ["Type", method.type],
    ["Provider", method.provider],
    ["Brand", method.brand],
    ["Last 4", method.last4 ? `••••${method.last4}` : null],
    ["Bank", method.bankName],
  ].filter(([, value]) => value !== null && value !== undefined && value !== "");
};

const ChargeDetailsDrawer = ({ charge, open, timeZone, onClose, onOpenBgp }) => {
  const bgpCode = bgpCodeOf(charge);
  const couponCodes = couponCodesOf(charge);
  const couponCode = couponCodes[0] || "";
  const appliedCoupons = appliedCouponsOf(charge);
  const couponLabel = couponLabelOf(charge);
  const discountAmount = discountAmountOf(charge);
  const discountRate = discountRateOf(charge);
  const currency = charge?.currency || "USD";

  const details = charge?.paymentChargeDetails || charge?.chargeDetails || null;
  const detailsRows = details
    ? Object.entries(details)
        .map(([key, value]) => [key, formatChargeDetailValue(key, value, currency)])
        .filter(([, value]) => value)
    : [];

  const mobileRows = [
    {
      key: "chargeType",
      label: "Charge Type",
      children: charge?.chargeItemCode ? prettyChargeItemLabel(charge.chargeItemCode) : null,
    },
    {
      key: "status",
      label: "Status",
      children: charge?.paymentStatus || charge?.status || "N/A",
    },
    {
      key: "date",
      label: "Date",
      children: fullLocalTimestamp(charge?.createdAt || charge?.updatedAt, timeZone),
    },
    {
      key: "receivableAmount",
      label: "Receivable Amount",
      children: money(incomingDetailAmount(charge), currency),
    },
    {
      key: "grossAmount",
      label: "Gross Amount",
      children: charge?.amount != null ? money(charge.amount, currency) : null,
    },
    {
      key: "chargeableAmount",
      label: "Chargeable Amount",
      children: charge?.chargeableAmount != null ? money(charge.chargeableAmount, currency) : null,
    },
    {
      key: "appliedCredits",
      label: "Applied Credits",
      children: charge?.appliedCreditsTotal != null ? money(charge.appliedCreditsTotal, currency) : null,
    },
    {
      key: "tip",
      label: "Tip",
      children: incomingTipAmount(charge) != null ? money(incomingTipAmount(charge), currency) : null,
    },
    {
      key: "currency",
      label: "Currency",
      children: charge?.currency ? String(charge.currency).toUpperCase() : null,
    },
    {
      key: "chargeItemId",
      label: "Charge Item ID",
      children: charge?.chargeItemId || null,
    },
    {
      key: "payer",
      label: "Payer",
      children: charge?.payer ? nameOf(charge.payer, "N/A") : null,
    },
    {
      key: "payee",
      label: "Payee",
      children: charge?.payee ? nameOf(charge.payee, "N/A") : null,
    },
  ].filter((item) => item.children !== null && item.children !== undefined && item.children !== "");

  const codeItems = [
    {
      key: "bgp",
      label: "BGP Code",
      children: bgpCode ? (
        <Button type="link" style={{ padding: 0, fontWeight: 700 }} onClick={() => onOpenBgp(bgpCode)}>
          {bgpCode}
        </Button>
      ) : "-",
    },
    {
      key: "coupon",
      label: "Coupon",
      children: couponCode ? (
        <Space direction="vertical" size={6}>
          <Space wrap size={[4, 4]}>
            {couponCodes.map((code) => (
              <Tag key={code} color="purple" style={{ marginInlineEnd: 0, fontWeight: 700 }}>
                {code}
              </Tag>
            ))}
          </Space>
          {couponLabel ? <Text type="secondary">{couponLabel}</Text> : null}
        </Space>
      ) : "-",
    },
    {
      key: "discount",
      label: "Discount",
      children: (
        <Space wrap size={6}>
          {discountRate !== null && discountRate !== undefined && discountRate !== "" ? (
            <Tag color="magenta">{discountRate}%</Tag>
          ) : null}
          {discountAmount !== null && discountAmount !== undefined && discountAmount !== "" ? (
            <Tag color="volcano">{money(discountAmount, currency)}</Tag>
          ) : null}
          {(discountRate === null || discountRate === undefined || discountRate === "") &&
          (discountAmount === null || discountAmount === undefined || discountAmount === "") ? (
            <Text type="secondary">-</Text>
          ) : null}
        </Space>
      ),
    },
    {
      key: "appliedCoupons",
      label: "Applied coupons",
      children: appliedCoupons.length ? (
        <Space direction="vertical" size={6}>
          {appliedCoupons.map((coupon, index) => (
            <Space key={coupon?.id || coupon?.code || index} wrap size={[4, 4]}>
              {coupon?.code ? <Tag color="purple">{coupon.code}</Tag> : null}
              {coupon?.discountType ? <Tag>{readable(coupon.discountType)}</Tag> : null}
              {coupon?.discountValue !== undefined && coupon?.discountValue !== null ? (
                <Tag color="magenta">{coupon.discountValue}{coupon.discountType === "PERCENTAGE" ? "%" : ""}</Tag>
              ) : null}
              {coupon?.discountAmount !== undefined && coupon?.discountAmount !== null ? (
                <Tag color="volcano">{money(coupon.discountAmount, currency)}</Tag>
              ) : null}
            </Space>
          ))}
        </Space>
      ) : "-",
    },
  ];

  return (
    <Drawer
      title="Charge details"
      open={open}
      onClose={onClose}
      width="min(560px, 100vw)"
      destroyOnClose
    >
      {charge ? (
        <Space direction="vertical" size={16} style={{ width: "100%" }}>
          <Card
            size="small"
            style={{
              ...workspaceStyles.softCard,
              background: "linear-gradient(135deg, rgba(27,174,66,0.12), rgba(0,136,204,0.08))",
            }}
          >
            <Space direction="vertical" size={10} style={{ width: "100%" }}>
              <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap">
                <Space direction="vertical" size={2}>
                  <Text type="secondary">Charge Details</Text>
                  <Text strong style={{ color: OLIVEEB_COLORS.oliveebDarkGreen, fontSize: 28, lineHeight: 1 }}>
                    {money(incomingDisplayAmount(charge), currency)}
                  </Text>
                </Space>
                <Tag color="green" style={{ marginInlineEnd: 0 }}>Earnings</Tag>
              </div>
              <Space wrap size={[6, 6]}>
                <Tag color="green">{readable(charge.paymentStatus || charge.status, "Status")}</Tag>
                {charge.chargeItemCode ? <Tag color="blue">{prettyChargeItemLabel(charge.chargeItemCode)}</Tag> : null}
                {charge.clientApp ? <Tag color="cyan">{readable(charge.clientApp)}</Tag> : null}
                {couponCodes.map((code) => <Tag key={code} color="purple">Coupon: {code}</Tag>)}
                {bgpCode ? <Tag color="cyan">BGP: {bgpCode}</Tag> : null}
              </Space>
            </Space>
          </Card>

          <Descriptions bordered size="small" column={1} items={mobileRows} />

          {charge.paymentMethod ? (
            <Card size="small" title="Payment Method" style={{ borderRadius: 8 }}>
              <Descriptions
                bordered
                size="small"
                column={1}
                items={paymentMethodRows(charge.paymentMethod).map(([label, value]) => ({
                  key: label,
                  label,
                  children: value,
                }))}
              />
            </Card>
          ) : null}

          {detailsRows.length ? (
            <Card size="small" title="Payment Charge Details" style={{ borderRadius: 8 }}>
              <Descriptions
                bordered
                size="small"
                column={1}
                items={detailsRows.map(([key, value]) => ({
                  key,
                  label: humanizeDetailLabel(key),
                  children: value,
                }))}
              />
            </Card>
          ) : null}

          <Descriptions bordered size="small" column={1} title="Codes and discounts" items={codeItems} />

          {details ? (
            <Collapse
              size="small"
              bordered
              items={[
                {
                  key: "additional-details",
                  label: "Raw details",
                  children: (
                    <pre style={{ margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: 12 }}>
                      {jsonPreview(details)}
                    </pre>
                  ),
                },
              ]}
            />
          ) : null}
        </Space>
      ) : null}
    </Drawer>
  );
};

const WorkspaceChargesPage = () => {
  const { workspaceId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const actorId = user?.id || user?.userId || "";
  const [account, setAccount] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [charges, setCharges] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState(defaultDateRange);
  const [selectedTimeZone, setSelectedTimeZone] = useState(DEFAULT_TIME_ZONE);
  const [selectedCharge, setSelectedCharge] = useState(null);
  const dateRangeRef = useRef(dateRange);

  const loadCharges = useCallback(async (nextPage = 1, rangeOverride) => {
    if (!workspaceId || !actorId) return;

    try {
      setLoading(true);
      const range = rangeOverride || dateRangeRef.current;
      const rangeParams = dateRangeToParams(range);
      const nextAccount = await fetchWorkspace(workspaceId, actorId);
      setAccount(nextAccount);

      let nextOrganization = null;
      if (!isPersonalAccount(nextAccount) && nextAccount.organizationId) {
        try {
          nextOrganization = await olreadiaService.organizations.get(nextAccount.organizationId);
        } catch (error) {
          nextOrganization = null;
        }
      }
      setOrganization(nextOrganization);

      const common = {
        actorId,
        workspaceId,
        start: rangeParams.start,
        end: rangeParams.end,
        page: nextPage - 1,
        size: PAGE_SIZE,
      };

      let result = { content: [] };
      if (isPersonalAccount(nextAccount)) {
        result = await paymentChargesService.outgoing.listForUser({
          ...common,
          userId: activeAccountUserId(nextAccount, actorId),
        });
      } else if (nextAccount.organizationId) {
        result = await paymentChargesService.outgoing.listForUser({
          ...common,
          userId: actorId,
          organizationId: nextAccount.organizationId,
        });
      }

      setCharges(pageContent(result));
      setTotal(pageTotal(result));
      setPage(nextPage);
    } catch (error) {
      message.error(error?.message || "Failed to load charges.");
    } finally {
      setLoading(false);
    }
  }, [actorId, workspaceId]);

  useEffect(() => {
    loadCharges(1);
  }, [loadCharges]);

  const context = useMemo(() => {
    if (!account) {
      return { label: "Workspace", detail: "-", tone: "default" };
    }

    if (isPersonalAccount(account)) {
      const userId = activeAccountUserId(account, actorId);
      const membership = (account.users || []).find((member) => member?.user?.id === userId);
      return {
        label: "Personal user",
        detail: nameOf(membership?.user, userId || "User"),
        tone: "blue",
      };
    }

    return {
      label: "Organization",
      detail: account.organizationId
        ? organizationName(organization, account.organizationId)
        : "Not linked",
      tone: account.organizationId ? "green" : "gold",
    };
  }, [account, actorId, organization]);

  const totalAmount = useMemo(
    () => charges.reduce((sum, charge) => sum + Number(charge?.amount || 0), 0),
    [charges],
  );

  const totalChargeable = useMemo(
    () => charges.reduce((sum, charge) => sum + Number(charge?.chargeableAmount || 0), 0),
    [charges],
  );

  const updateDateRange = (nextRange) => {
    const normalizedRange = nextRange || defaultDateRange();
    dateRangeRef.current = normalizedRange;
    setDateRange(normalizedRange);
    loadCharges(1, normalizedRange);
  };

  const openBgpCode = (bgpCode) => {
    if (!bgpCode) return;
    navigate(`${paths.partnerCommissions}?bgpCode=${encodeURIComponent(bgpCode)}`);
  };

  const columns = [
    {
      title: "Charge",
      key: "charge",
      width: 420,
      render: (_, charge) => (
        <div style={{ minWidth: 0 }}>
          <Text strong style={{ display: "block", wordBreak: "break-word" }}>
            {charge.description || readable(charge.chargeItemCode, "Charge")}
          </Text>
          <Space wrap size={[4, 4]} style={{ marginTop: 4 }}>
            {charge.chargeItemCode ? <Tag color="blue">{readable(charge.chargeItemCode)}</Tag> : null}
            {charge.clientApp ? <Tag color="cyan">{readable(charge.clientApp)}</Tag> : null}
          </Space>
        </div>
      ),
    },
    {
      title: "Amount",
      key: "amount",
      width: 130,
      render: (_, charge) => (
        <div style={{ whiteSpace: "nowrap" }}>
          <Text strong>{money(charge.chargeableAmount ?? charge.amount, charge.currency)}</Text>
          <Text type="secondary" style={{ display: "block", fontSize: 12 }}>
            Base {money(charge.amount, charge.currency)}
          </Text>
        </div>
      ),
    },
    {
      title: "Status",
      key: "status",
      width: 120,
      render: (_, charge) => (
        <Space direction="vertical" size={2}>
          <Tag color="green" style={{ marginInlineEnd: 0 }}>
            {readable(charge.paymentStatus || charge.status, "Status")}
          </Tag>
          {charge.status ? (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {readable(charge.status)}
            </Text>
          ) : null}
        </Space>
      ),
    },
    {
      title: "Codes",
      key: "codes",
      width: 190,
      render: (_, charge) => {
        const bgpCode = bgpCodeOf(charge);
        const couponCodes = couponCodesOf(charge);
        return bgpCode || couponCodes.length ? (
          <Space wrap size={[4, 4]}>
            {bgpCode ? (
              <Tag
                color="cyan"
                style={{ cursor: "pointer", marginInlineEnd: 0, fontWeight: 700 }}
                onClick={(event) => {
                  event.stopPropagation();
                  openBgpCode(bgpCode);
                }}
              >
                BGP: {bgpCode}
              </Tag>
            ) : null}
            {couponCodes.map((code) => (
              <Tag key={code} color="purple" style={{ marginInlineEnd: 0, fontWeight: 700 }}>
                Coupon: {code}
              </Tag>
            ))}
          </Space>
        ) : (
          <Text type="secondary">-</Text>
        );
      },
    },
    {
      title: "Parties",
      key: "parties",
      width: 200,
      render: (_, charge) => (
        <div style={{ minWidth: 0 }}>
          <Text ellipsis style={{ display: "block" }}>Payer: {nameOf(charge.payer, "Unknown")}</Text>
          <Text ellipsis type="secondary" style={{ display: "block", fontSize: 12 }}>
            Payee: {nameOf(charge.payee, "Unknown")}
          </Text>
        </div>
      ),
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 230,
      render: (value) => (
        <Text style={{ whiteSpace: "nowrap" }}>
          {fullLocalTimestamp(value, selectedTimeZone)}
        </Text>
      ),
    },
  ];

  return (
    <div className="project-page">
      <div className="d-flex justify-content-between align-items-center mb-3 mt-3 gap-3 flex-wrap">
        <WidgetSectionTitle
          icon="dollar-sign"
          title="Workspace Charges"
          subtitle="Review charge details for the attached organization or personal workspace user"
          className="flex-1"
          titleStyle={{ color: OLIVEEB_COLORS.oliveebDarkGreen }}
          subtitleStyle={{ color: OLIVEEB_COLORS.oliveebGreen }}
        />
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(paths.workspaceDetails(workspaceId))}>
            Back
          </Button>
          <Button
            icon={<ReloadOutlined />}
            style={workspaceButtonStyles.refresh}
            onClick={() => loadCharges(page)}
          >
            Refresh
          </Button>
        </Space>
      </div>

      {loading && !account ? (
        <Card>
          <Skeleton active paragraph={{ rows: 8 }} />
        </Card>
      ) : !account ? (
        <Card>
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Workspace not found" />
        </Card>
      ) : (
        <Space direction="vertical" size={16} style={{ width: "100%" }}>
          {!isPersonalAccount(account) && !account.organizationId ? (
            <Alert
              type="warning"
              showIcon
              message="This organization workspace is not linked to an organization."
              description="Charges can be loaded after an organization is attached."
            />
          ) : null}

          <Card size="small" style={workspaceStyles.softCard}>
            <div className="d-flex justify-content-between align-items-center gap-3 flex-wrap">
              <Space size={8} wrap>
                <DollarOutlined style={{ color: OLIVEEB_COLORS.oliveebGreen }} />
                <Text strong style={{ color: OLIVEEB_COLORS.oliveebDarkGreen }}>
                  {context.label}
                </Text>
                <Tag color={context.tone}>{context.detail}</Tag>
                <Tag color="blue">{dateRangeLabel(dateRange)}</Tag>
                <Tag color="cyan">{selectedTimeZone}</Tag>
              </Space>
              <Space size={8} wrap>
                <Text type="secondary">{total} charges</Text>
                <Text strong>{money(totalChargeable || totalAmount, charges[0]?.currency || "USD")}</Text>
              </Space>
            </div>
          </Card>

          <Card size="small" style={{ ...workspaceStyles.softCard, background: OLIVEEB_COLORS.card }}>
            <div className="d-flex justify-content-between align-items-center gap-3 flex-wrap">
              <Space direction="vertical" size={2}>
                <Text strong style={{ color: OLIVEEB_COLORS.oliveebDarkGreen }}>
                  Charge date range
                </Text>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {dateRangeLabel(dateRange)}
                </Text>
              </Space>
              <Space wrap>
                <Select
                  showSearch
                  value={selectedTimeZone}
                  options={TIME_ZONE_OPTIONS}
                  style={{ width: 240 }}
                  onChange={setSelectedTimeZone}
                  optionFilterProp="label"
                />
                <RangePicker
                  value={dateRange}
                  allowClear={false}
                  showTime={{
                    defaultValue: [dayjs().startOf("day"), lastSecondOfDay(dayjs())],
                  }}
                  format="MMM D, YYYY HH:mm:ss"
                  onChange={updateDateRange}
                  disabledDate={(current) => current && current.isAfter(dayjs().endOf("day"))}
                  presets={[
                    { label: "Last 7 days", value: [dayjs().subtract(7, "day").startOf("day"), lastSecondOfDay(dayjs())] },
                    { label: "Last 30 days", value: [dayjs().subtract(30, "day").startOf("day"), lastSecondOfDay(dayjs())] },
                    { label: "Last 90 days", value: defaultDateRange() },
                    { label: "This month", value: [dayjs().startOf("month"), lastSecondOfDay(dayjs())] },
                  ]}
                />
                <Button onClick={() => updateDateRange([dayjs().subtract(7, "day").startOf("day"), lastSecondOfDay(dayjs())])}>
                  7d
                </Button>
                <Button onClick={() => updateDateRange([dayjs().subtract(30, "day").startOf("day"), lastSecondOfDay(dayjs())])}>
                  30d
                </Button>
                <Button onClick={() => updateDateRange(defaultDateRange())}>
                  90d
                </Button>
              </Space>
            </div>
          </Card>

          <Card
            styles={{ body: { padding: 0 } }}
            style={{
              borderRadius: 8,
              overflow: "hidden",
              borderColor: OLIVEEB_COLORS.border,
              background: OLIVEEB_COLORS.card,
            }}
          >
            <Table
              rowKey={(record) => record.id || record.chargeItemId || record.createdAt}
              loading={loading}
              columns={columns}
              dataSource={charges}
              size="small"
              tableLayout="fixed"
              pagination={{
                current: page,
                pageSize: PAGE_SIZE,
                total,
                showSizeChanger: false,
                onChange: (nextPage) => loadCharges(nextPage),
              }}
              scroll={{ x: 1260 }}
              locale={{
                emptyText: (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="No charges found for this workspace context"
                    style={{ padding: "40px 0" }}
                  />
                ),
              }}
              onRow={(record) => ({
                onClick: () => setSelectedCharge(record),
                style: { cursor: "pointer" },
              })}
            />
          </Card>
        </Space>
      )}
      <ChargeDetailsDrawer
        charge={selectedCharge}
        open={Boolean(selectedCharge)}
        timeZone={selectedTimeZone}
        onClose={() => setSelectedCharge(null)}
        onOpenBgp={openBgpCode}
      />
    </div>
  );
};

export default WorkspaceChargesPage;
